"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { trackBetLinkClick, trackToolRowExpanded } from "@/utils/analytics";
import {
  type MarketOpportunity,
  type PeerOdds,
  formatCurrency,
  formatClassicSideName,
  capitalizeMarket,
  formatClassicMarketTitle,
  formatGameTime,
  getFairOutcomeShare,
  getWhaleVolumeDisplay,
  sideSharePct,
  edgeLabel,
  getWhaleDemandFromOpposite,
  getWhaleWatchTrackingProps,
  getClassicSecondaryBooksForSide,
  getClassicShortSideLabel,
  getClassicBookAvailableLiquidity,
  formatClassicSportAndLeague,
} from "@/lib/whale-watch/whale-watch-shared";
import { ClassicStackedBookLogos } from "@/components/whale-watch/whale-watch-book-logo";
import { OddsBadge } from "@/components/ui/odds-badge";
import { BookLiquidityLabel } from "./peer-odds-cell";
import { EyeOff } from "lucide-react";

const EDGE_BADGE_CLASS = {
  value: "bg-accent-value/20 text-accent-value border border-accent-value/30",
  negative: "bg-destructive/20 text-destructive border border-destructive/30",
  quiet: "bg-white/[0.06] text-zinc-300 border border-white/10",
} as const;

export const OpportunityCard = React.memo(function OpportunityCard({
  opp,
  isSelected,
  onSelect,
  selectedBookKeys = [],
  onDismiss,
  practice = false,
}: {
  opp: MarketOpportunity<PeerOdds>;
  isSelected: boolean;
  onSelect: (id: string) => void;
  selectedBookKeys?: string[];
  onDismiss?: (opp: MarketOpportunity) => void;
  practice?: boolean;
}) {
  const market = opp.market;
  if (!market || market.sides.length < 2) return null;

  const [side1, side2] = market.sides;
  const edge = edgeLabel(side1.edge_percent);
  const secondaryBooks = getClassicSecondaryBooksForSide(side1.peers, selectedBookKeys, side1.best_book || "", side2.books, getFairOutcomeShare(market), side1.best_odds);

  const side1WhaleDemand = getWhaleDemandFromOpposite(side2.books);
  const side2WhaleDemand = getWhaleDemandFromOpposite(side1.books);
  const share = sideSharePct([side1WhaleDemand, side2WhaleDemand], [side1.actual_money_at_risk, side2.actual_money_at_risk]);
  const opportunityActionLabel = `${isSelected ? "Deselect" : "Select"} ${opp.event} ${capitalizeMarket(market.market)} ${formatClassicSideName(side1, market.market)} opportunity`;
  const selectOpportunity = () => {
    if (!isSelected && !practice) {
      trackToolRowExpanded({ tool: "whale-watch", row_kind: "opportunity" });
    }
    onSelect(opp.id);
  };

  const whaleVolume = getWhaleVolumeDisplay(market);
  const playLiquidity = getClassicBookAvailableLiquidity(side1, side1.best_book);

  return (
    <section
      onClick={selectOpportunity}
      className={cn(
        "relative border rounded-panel p-3 cursor-pointer transition-all duration-200",
        isSelected
          ? "bg-surface-raised border-accent-value/30 shadow-card-selected"
          : "bg-surface-raised border-white/[0.08] hover:border-white/15 active:scale-[0.98]"
      )}
    >
      <button
        type="button"
        aria-pressed={isSelected}
        aria-label={opportunityActionLabel}
        onClick={(event) => {
          event.stopPropagation();
          selectOpportunity();
        }}
        className="sr-only focus:not-sr-only focus:absolute focus:z-10 focus:rounded-md focus:bg-surface-raised focus:px-2 focus:py-1 focus:text-xs focus:text-foreground focus:ring-2 focus:ring-primary/40"
      >
        {opportunityActionLabel}
      </button>
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="data-hero text-3xl text-accent-value">
              {formatCurrency(whaleVolume.value, true)}
            </span>
            <span className={cn("px-2 py-1 text-xs font-semibold data-num tabular-nums rounded", EDGE_BADGE_CLASS[edge.tone])}>
              {edge.text}
            </span>
          </div>
          <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {whaleVolume.mode === "edge_supporting"
              ? "Whale $ behind this play"
              : "Whale $ on this side"}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-medium text-muted-foreground">{practice ? "Practice game" : formatGameTime(opp.event_start)}</span>
          {onDismiss && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onDismiss(opp);
              }}
              onKeyDown={(event) => event.stopPropagation()}
              className={cn(
                "inline-flex h-7 w-7 items-center justify-center rounded-lg",
                "text-zinc-500 hover:text-zinc-200 hover:bg-zinc-700/50",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                "transition-colors"
              )}
              aria-label={`Hide ${formatClassicSideName(side1, market.market)} opportunity`}
              title="Hide opportunity"
            >
              <EyeOff className="size-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="mb-2.5">
        <div className="text-sm font-semibold text-foreground truncate">{opp.event}</div>
        <div className="text-[11px]">
          <span className="text-muted-foreground">{formatClassicSportAndLeague(opp.sport, opp.league)} • </span>
          <span className="text-zinc-300 font-medium">{formatClassicMarketTitle(market.market, market.market_arity)}</span>
        </div>
      </div>

      <div className="rounded-md p-2.5 mb-2 border border-white/[0.08] bg-white/[0.03]">
        <div className="flex items-center gap-2.5">
          {side1.best_book && (
            <ClassicStackedBookLogos bestBook={side1.best_book} secondaryBooks={secondaryBooks} />
          )}
          <span className="text-sm font-medium text-foreground flex-1 min-w-0 truncate">
            {formatClassicSideName(side1, market.market)}
          </span>
          <div className="flex flex-col items-end gap-0.5 shrink-0">
            <OddsBadge odds={side1.best_odds} variant="small" highlight showTooltip betLink={side1.best_bet_link} onBetLinkClick={() => {
              trackBetLinkClick(getWhaleWatchTrackingProps(opp, {
                sportsbook: side1.best_book || "unknown",
                odds_american: side1.best_odds,
                ev_percentage: side1.edge_percent,
                click_source: "odds_badge",
              }));
            }} />
            <BookLiquidityLabel amount={playLiquidity} />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-2.5 px-0.5">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] text-accent-value font-medium mb-1 truncate">
            {getClassicShortSideLabel(side1, market.market)}
          </div>
          <div className="h-2 rounded-full bg-zinc-800/60 overflow-hidden">
            {share !== null && (
              <div
                className="h-full rounded-full bg-accent-value/90"
                style={{ width: `${Math.max(share, 8)}%` }}
              />
            )}
          </div>
        </div>

        <span className="text-[9px] text-zinc-600 shrink-0">vs</span>

        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-medium text-muted-foreground mb-1 truncate">
            {getClassicShortSideLabel(side2, market.market)}
          </div>
          <div className="h-2 rounded-full bg-zinc-800/60 overflow-hidden">
            {share !== null && (
              <div
                className="h-full rounded-full bg-zinc-600"
                style={{ width: `${Math.max(100 - share, 8)}%` }}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
});
